/**
 * Citations composition method
 * 
 * Author: Sahil David <sahil.david@huumun.com>
 */

import { onMounted, ref, toRefs, watchEffect, watch } from 'vue';

import { getLyraCitation } from '@/core/modules/lyra/services';
import { sortMappedArray } from '@/core/services/core.utils';
import citationsConfig from '../config/citations.config';

import { ICitationsTypes } from '../@types';

export function citationsComposition (props: any) {
	const { citations } = toRefs(props);

	// Data citations type
	const citationsType = ref<string[]>([]);
	const activeCitationType = ref<string>('');
	const citationsData = ref<any>([]);

	// Watcher
	watch(() => citations.value, () => {
		deriveData();
	})

	watchEffect(() => {
		if (!activeCitationType.value && (citations as any).length > 0) {
			defaultActiveCitation();
		}
	});

	// Lifecycle
	onMounted(() => {
		deriveData();
	})

	// Function make first type as active 
	function defaultActiveCitation () {
		const [firstValue] = citationsType.value;
		activeCitationType.value = firstValue;
	}

	// Function to sort citations bases on config
	function sortCitationsType () {
		const types: string[] = citationsConfig
			.sort((a: any, b: any) => a.order - b.order)
			.map((_: ICitationsTypes) => _.type);

		citationsType.value = sortMappedArray(citationsType.value, types);
	}

	// Function to derive citations data
	function deriveData () {
		citationsType.value = [];
		const datum: any[] = [];

		(citations.value || []).forEach((citation: string) => {
			const obj = getLyraCitation(citation);
			if (!obj) { return; }

			const { type } = obj;
			if (!citationsType.value.includes(type)) { citationsType.value.push(type); }

			datum.push(obj);
		});
		citationsData.value = datum;

		sortCitationsType();
		defaultActiveCitation();
	}

	// Function to make citation active 
	function makeCitationsActive (citation: string) {
		if (citation) {
			activeCitationType.value = citation;
		}
	}

	// Function to get citations data 
	function getCitationsContent (citation = ''): any[] {
		if (!activeCitationType.value) { return []; }
		const activeCitation = citation !== '' ? citation : activeCitationType.value;

		let datum = (citationsData.value || []).filter((_: any) => _.type === activeCitation);
		const config = citationsConfigFn();

		const { sort } = config as ICitationsTypes;
		if (sort) {
			datum = (datum || []).sort((a: any, b: any) => a.key.localeCompare(b.key));
		}

		return datum;
	}

	// Function to get citations config
	function citationsConfigFn () {
		return citationsConfig.find((_: any) => _.type === activeCitationType.value);
	}

	// Function to render component
	function rendererFunction () {
		return citationsConfigFn()?.renderer;
	}

	return {
		citationsType,
		activeCitationType,
		citationsData,
		makeCitationsActive,
		getCitationsContent,
		rendererFunction
	};
}
